import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import type { AxiosProgressEvent } from 'axios';
import { EASE_OUT } from '@/lib/motion';
import StepCard from '../../../courses/wizard/shared/StepCard';
import DropZone from '../../../courses/wizard/shared/DropZone';
import {
  useProgram,
  useBatchUpdateDays,
  useAddDayCourse,
  useRemoveDayCourse,
  useUpdateProgram,
} from '../../../../services/programs';
import { useCourses } from '../../../../services/courses';
import { uploadFile, getSignedUrl, useImageUrl } from '../../../../services/uploads';
import type { ProgramDayCourse } from '../../../../types/program';
import type { Course } from '../../../../types/course';
import { palette } from '@/styles/figma-tokens';

// ─── Constants ────────────────────────────────────────────────────────────────

const MAX_IMAGE_SIZE_MB = 5;
const ALLOWED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

// ─── Inline styles ────────────────────────────────────────────────────────────

const inputStyle: React.CSSProperties = {
  width: '100%',
  border: '1px solid rgb(var(--color-border-primary) / 0.45)',
  borderRadius: '8px',
  padding: '10px 14px',
  fontFamily: 'Poppins, sans-serif',
  fontSize: '14px',
  lineHeight: '22px',
  color: 'rgb(var(--color-text-primary))',
  background: 'rgb(var(--color-surface-primary))',
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.15s',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: 'Poppins, sans-serif',
  fontSize: '12px',
  fontWeight: 600,
  lineHeight: '18px',
  color: 'rgb(var(--color-text-secondary))',
  marginBottom: '6px',
};

// ─── Types ────────────────────────────────────────────────────────────────────

interface DayLocalState {
  dayId: string | null; // null if not yet created by API
  dayNumber: number;
  title: string;
  headerImageKey: string | null;
  headerImagePreviewUrl: string | null;
  headerImageFileName: string | null;
  headerImageFileSize: number | null;
  headerImageUploading: boolean;
  headerImageUploadProgress: number;
  headerImageUploadError: string | null;
  courses: ProgramDayCourse[];
}

interface CoursePickerModalProps {
  dayNumber: number;
  onSelect: (course: Course) => void;
  onClose: () => void;
  alreadyAddedCourseIds: Set<string>;
}

// ─── Course thumbnail ─────────────────────────────────────────────────────────

interface CourseThumbProps {
  imageKey: string | null | undefined;
  size: 'sm' | 'md';
}

function CourseThumb({ imageKey, size }: CourseThumbProps) {
  const [resolvedUrl] = useImageUrl(imageKey ?? null);
  const dim = size === 'sm' ? 'w-10 h-10' : 'w-12 h-12';
  const iconSize = size === 'sm' ? 16 : 20;

  return (
    <div className={`${dim} rounded-lg overflow-hidden bg-neutral-100 dark:bg-neutral-800 shrink-0`}>
      {resolvedUrl ? (
        <img src={resolvedUrl} alt="" className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full flex items-center justify-center">
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="3" stroke={palette.grey['600']} strokeWidth="1.5" />
            {size === 'md' && <path d="M9 9h6M9 13h4" stroke={palette.grey['600']} strokeWidth="1.5" strokeLinecap="round" />}
          </svg>
        </div>
      )}
    </div>
  );
}

// ─── Course picker modal ──────────────────────────────────────────────────────

function CoursePickerModal({
  dayNumber,
  onSelect,
  onClose,
  alreadyAddedCourseIds,
}: CoursePickerModalProps) {
  const [search, setSearch] = useState('');
  const { data, isLoading } = useCourses({ search: search || undefined, per_page: 50 });
  const courses = data?.courses ?? [];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="bg-surface-primary rounded-2xl shadow-2xl flex flex-col"
        style={{ width: '560px', maxHeight: '80vh' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-neutral-100 dark:border-neutral-800">
          <h3 className="text-[17px] font-bold text-text-primary">
            Add Course — Day {dayNumber}
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 rounded-full hover:bg-neutral-100 dark:hover:bg-neutral-800 flex items-center justify-center transition-colors"
            aria-label="Close"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        {/* Search */}
        <div className="px-6 pt-4 pb-3">
          <input
            type="text"
            placeholder="Search courses..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={inputStyle}
            onFocus={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = 'rgb(var(--color-primary))'; }}
            onBlur={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = 'rgb(var(--color-border-primary) / 0.45)'; }}
          />
        </div>

        {/* Course list */}
        <div className="overflow-y-auto flex-1 px-6 pb-5">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="w-7 h-7 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : courses.length === 0 ? (
            <p className="text-center text-sm text-text-secondary py-10">
              {search ? 'No courses match your search.' : 'No courses available yet.'}
            </p>
          ) : (
            <div className="flex flex-col gap-2 pt-1">
              {courses.map((course) => {
                const alreadyAdded = alreadyAddedCourseIds.has(course.id);
                return (
                  <button
                    key={course.id}
                    type="button"
                    disabled={alreadyAdded}
                    onClick={() => {
                      if (!alreadyAdded) {
                        onSelect(course);
                        onClose();
                      }
                    }}
                    className={`w-full flex items-center gap-3 px-4 py-3 rounded-full text-left border transition-all ${
                      alreadyAdded
                        ? 'border-neutral-100 dark:border-neutral-800 opacity-50 cursor-not-allowed bg-neutral-50 dark:bg-neutral-800/30'
                        : 'border-neutral-100 dark:border-neutral-800 hover:border-primary-300 hover:bg-primary-50/40 dark:hover:bg-primary-500/10 cursor-pointer'
                    }`}
                  >
                    {/* Course thumbnail */}
                    <CourseThumb imageKey={course.image_url} size="md" />

                    {/* Title + status + duration */}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="text-sm font-semibold text-text-primary truncate">{course.title}</p>
                        <span
                          className={`shrink-0 text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${
                            course.status === 'published'
                              ? 'bg-success-500/10 text-success-600 dark:text-success-400'
                              : course.status === 'draft'
                                ? 'bg-warning-500/10 text-warning-600 dark:text-warning-400'
                                : 'bg-text-tertiary/10 text-text-secondary'
                          }`}
                        >
                          {course.status === 'published'
                            ? 'Published'
                            : course.status === 'draft'
                              ? 'Draft'
                              : 'Hidden'}
                        </span>
                      </div>
                      {course.estimated_duration_minutes !== null && (
                        <p className="text-xs text-text-secondary mt-0.5">
                          {course.estimated_duration_minutes} min
                        </p>
                      )}
                    </div>

                    {alreadyAdded && (
                      <span className="text-xs font-medium text-primary-500 shrink-0">Added</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Day card component ───────────────────────────────────────────────────────

interface DayCardProps {
  day: DayLocalState;
  onTitleChange: (dayNumber: number, title: string) => void;
  onImageSelected: (dayNumber: number, files: File[]) => void;
  onRemoveImage: (dayNumber: number) => void;
  onAddCourse: (dayNumber: number) => void;
  onRemoveCourse: (dayNumber: number, dayCourse: ProgramDayCourse) => void;
}

function DayCard({
  day,
  onTitleChange,
  onImageSelected,
  onRemoveImage,
  onAddCourse,
  onRemoveCourse,
}: DayCardProps) {
  return (
    <div className="bg-surface-primary rounded-2xl border border-[rgba(71,70,76,0.1)] dark:border-neutral-800 overflow-hidden">
      {/* Day header band */}
      <div
        className="px-6 py-4 flex items-center gap-4"
        style={{ background: 'linear-gradient(to right, rgb(var(--color-primary) / 0.08), rgb(var(--color-secondary) / 0.08))' }}
      >
        {/* Day badge */}
        <div className="shrink-0 w-10 h-10 rounded-full bg-primary-500 flex items-center justify-center">
          <span className="text-sm font-bold">{day.dayNumber}</span>
        </div>

        {/* Day title input */}
        <div className="flex-1">
          <input
            type="text"
            placeholder={`Day ${day.dayNumber} title (optional)`}
            value={day.title}
            onChange={(e) => onTitleChange(day.dayNumber, e.target.value)}
            style={{
              ...inputStyle,
              padding: '8px 12px',
              fontSize: '15px',
              fontWeight: 600,
            }}
            onFocus={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = 'rgb(var(--color-primary))'; }}
            onBlur={(e) => { (e.currentTarget as HTMLInputElement).style.borderColor = 'rgb(var(--color-border-primary) / 0.45)'; }}
          />
        </div>
      </div>

      {/* Body */}
      <div className="px-6 py-5 flex flex-col gap-5">

        {/* Header image */}
        <div>
          <p style={labelStyle}>Day Header Image (optional)</p>
          {day.headerImagePreviewUrl ? (
            <div className="rounded-xl border border-neutral-100 dark:border-neutral-800 overflow-hidden">
              <img
                src={day.headerImagePreviewUrl}
                alt={`Day ${day.dayNumber} header preview`}
                className="w-full h-32 object-cover"
              />
              <div className="flex items-center justify-between px-4 py-2.5 gap-3">
                <div className="min-w-0">
                  <p className="text-[13px] font-semibold text-text-primary truncate">
                    {day.headerImageFileName}
                  </p>
                  {day.headerImageFileSize !== null && (
                    <p className="text-xs text-text-secondary mt-0.5">
                      {(day.headerImageFileSize / 1024 / 1024).toFixed(2)} MB
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => onRemoveImage(day.dayNumber)}
                  disabled={day.headerImageUploading}
                  className="text-[13px] font-semibold text-error-500 hover:opacity-80 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed shrink-0"
                >
                  Remove
                </button>
              </div>
              {day.headerImageUploading && (
                <div className="px-4 pb-3">
                  <div className="h-1 bg-primary-100 dark:bg-primary-900/40 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-full transition-all"
                      style={{ width: `${day.headerImageUploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-text-secondary mt-1">
                    Uploading... {day.headerImageUploadProgress}%
                  </p>
                </div>
              )}
            </div>
          ) : (
            <DropZone
              onFilesSelected={(files) => onImageSelected(day.dayNumber, files)}
              accept="image/jpeg,image/png,image/webp"
              maxSizeMB={MAX_IMAGE_SIZE_MB}
              label="Drop or select day image"
            />
          )}
          {day.headerImageUploadError && (
            <p role="alert" className="text-xs text-error-500 mt-1">
              {day.headerImageUploadError}
            </p>
          )}
        </div>

        {/* Courses in this day */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <p style={labelStyle}>Courses</p>
            <button
              type="button"
              onClick={() => onAddCourse(day.dayNumber)}
              className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary-500 hover:text-primary-600 dark:hover:text-primary-350 transition-colors"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
              Add Course
            </button>
          </div>

          {day.courses.length === 0 ? (
            <div className="rounded-xl border border-dashed border-neutral-200 dark:border-neutral-700 py-8 text-center">
              <p className="text-sm text-text-secondary">No courses yet. Click "Add Course" to assign one.</p>
            </div>
          ) : (
            <div className="flex flex-col gap-2">
              {day.courses
                .slice()
                .sort((a, b) => a.order_index - b.order_index)
                .map((dayCourse) => (
                  <div
                    key={dayCourse.id}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl bg-neutral-50 dark:bg-neutral-800/40 border border-neutral-100 dark:border-neutral-800 group"
                  >
                    {/* Drag handle */}
                    <div className="shrink-0 text-neutral-300 dark:text-neutral-600 cursor-grab active:cursor-grabbing">
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <circle cx="9" cy="7" r="1.5" fill="currentColor" />
                        <circle cx="9" cy="12" r="1.5" fill="currentColor" />
                        <circle cx="9" cy="17" r="1.5" fill="currentColor" />
                        <circle cx="15" cy="7" r="1.5" fill="currentColor" />
                        <circle cx="15" cy="12" r="1.5" fill="currentColor" />
                        <circle cx="15" cy="17" r="1.5" fill="currentColor" />
                      </svg>
                    </div>

                    {/* Order badge */}
                    <span className="shrink-0 w-5 h-5 rounded-full bg-primary-100 dark:bg-primary-500/20 text-primary-600 dark:text-primary-400 text-[11px] font-bold flex items-center justify-center">
                      {dayCourse.order_index + 1}
                    </span>

                    {/* Course thumbnail */}
                    <CourseThumb imageKey={dayCourse.course?.image_url ?? null} size="sm" />

                    {/* Title + duration */}
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-text-primary truncate">
                        {dayCourse.course?.title ?? 'Unknown course'}
                      </p>
                      {dayCourse.course?.estimated_duration_minutes !== null && dayCourse.course !== null && (
                        <p className="text-xs text-text-secondary mt-0.5">
                          {dayCourse.course.estimated_duration_minutes} min
                        </p>
                      )}
                    </div>

                    {/* Remove */}
                    <button
                      type="button"
                      onClick={() => onRemoveCourse(day.dayNumber, dayCourse)}
                      className="shrink-0 opacity-0 group-hover:opacity-100 w-7 h-7 rounded-full hover:bg-error-50 dark:hover:bg-error-500/10 flex items-center justify-center transition-all"
                      aria-label={`Remove ${dayCourse.course?.title ?? 'course'}`}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M18 6L6 18M6 6l12 12" stroke={palette.error['500']} strokeWidth="2.5" strokeLinecap="round" />
                      </svg>
                    </button>
                  </div>
                ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Duration stepper ─────────────────────────────────────────────────────────

const MIN_DURATION = 1;
const MAX_DURATION = 365;

function emptyDay(dayNumber: number): DayLocalState {
  return {
    dayId: null,
    dayNumber,
    title: `Day ${dayNumber}`,
    headerImageKey: null,
    headerImagePreviewUrl: null,
    headerImageFileName: null,
    headerImageFileSize: null,
    headerImageUploading: false,
    headerImageUploadProgress: 0,
    headerImageUploadError: null,
    courses: [],
  };
}

/** Grow (append empty day slots) or shrink (drop from the tail) to `next` days. */
function resizeDays(prev: DayLocalState[], next: number): DayLocalState[] {
  if (next === prev.length) return prev;
  if (next < prev.length) return prev.slice(0, next);
  const grown = prev.slice();
  for (let i = prev.length + 1; i <= next; i++) grown.push(emptyDay(i));
  return grown;
}

interface DurationControlProps {
  value: string;
  dayCount: number;
  saving: boolean;
  locked: boolean;
  onInputChange: (value: string) => void;
  onCommitBlur: () => void;
  onStep: (delta: number) => void;
}

/**
 * The duration stepper that sits atop Step 2 and drives how many day slots the
 * program has. Editing it (typing + blur/Enter, or the −/+ steppers) is wired
 * by the parent to persist `duration_days` and scaffold/prune the day rows.
 */
function DurationControl({
  value,
  dayCount,
  saving,
  locked,
  onInputChange,
  onCommitBlur,
  onStep,
}: DurationControlProps) {
  const stepperBtn =
    'w-8 h-8 rounded-full flex items-center justify-center text-text-primary ' +
    'hover:bg-neutral-100 dark:hover:bg-neutral-800 active:scale-[0.94] ' +
    'transition-[background-color,transform] duration-150 ' +
    'disabled:opacity-40 disabled:cursor-not-allowed disabled:active:scale-100';

  return (
    <div className="rounded-2xl border border-[rgba(71,70,76,0.1)] dark:border-neutral-800 bg-neutral-50/70 dark:bg-neutral-800/30 px-6 py-5">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <p className="text-[15px] font-semibold text-text-primary">Program duration</p>
          <p className="text-[13px] text-text-secondary mt-0.5">
            Sets how many day slots this program has.
          </p>
        </div>

        <div className="flex items-center gap-3 shrink-0">
          <div className="flex items-center rounded-full border border-border-primary/30 bg-surface-primary p-1">
            <button
              type="button"
              aria-label="Decrease duration"
              onClick={() => onStep(-1)}
              disabled={locked || saving || dayCount <= MIN_DURATION}
              className={stepperBtn}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M5 12h14" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
              </svg>
            </button>
            <input
              type="number"
              min={MIN_DURATION}
              max={MAX_DURATION}
              value={value}
              disabled={locked || saving}
              onChange={(e) => onInputChange(e.target.value)}
              onBlur={onCommitBlur}
              onKeyDown={(e) => {
                if (e.key === 'Enter') (e.currentTarget as HTMLInputElement).blur();
              }}
              aria-label="Program duration in days"
              className="w-14 text-center bg-transparent outline-none text-[15px] font-bold text-text-primary disabled:opacity-50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <button
              type="button"
              aria-label="Increase duration"
              onClick={() => onStep(1)}
              disabled={locked || saving || dayCount >= MAX_DURATION}
              className={stepperBtn}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path
                  d="M12 5v14M5 12h14"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
          <span className="text-[13px] font-medium text-text-secondary min-w-[58px]">
            {saving ? 'Saving…' : dayCount === 1 ? '1 day' : `${dayCount} days`}
          </span>
        </div>
      </div>
    </div>
  );
}

interface ShrinkConfirmModalProps {
  nextDuration: number;
  lostDays: number[];
  onConfirm: () => void;
  onCancel: () => void;
}

/** Confirmation shown before a shrink drops day slots that already have courses. */
function ShrinkConfirmModal({
  nextDuration,
  lostDays,
  onConfirm,
  onCancel,
}: ShrinkConfirmModalProps) {
  const many = lostDays.length > 1;
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.45)' }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div className="bg-surface-primary rounded-2xl shadow-2xl w-[440px] max-w-[90vw] p-6">
        <h3 className="text-[17px] font-bold text-text-primary mb-2">Reduce duration?</h3>
        <p className="text-sm text-text-secondary leading-relaxed mb-6">
          Shortening to {nextDuration} {nextDuration === 1 ? 'day' : 'days'} removes{' '}
          {many ? 'days' : 'day'} {lostDays.join(', ')}, including the {many ? 'courses' : 'course'}{' '}
          you added there. This can&apos;t be undone.
        </p>
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-full text-sm font-bold text-text-primary border border-border-primary/30 hover:bg-neutral-100 dark:hover:bg-neutral-800 active:scale-[0.97] transition-[background-color,transform] duration-150"
          >
            Keep days
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="px-4 py-2 rounded-full text-sm font-bold text-white bg-error-500 hover:brightness-105 active:scale-[0.97] transition-[filter,transform] duration-150"
          >
            Remove days
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Props ────────────────────────────────────────────────────────────────────

interface ProgramDaysStepProps {
  programId: string;
  disabled?: boolean;
  onSaved: () => void;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * ProgramDaysStep — Step 2 of the program creation wizard.
 * Shows one card per day (determined by duration_days from the program).
 * Each day has an editable title, optional header image, and a list of courses.
 */
export default function ProgramDaysStep({
  programId,
  disabled = false,
  onSaved,
}: ProgramDaysStepProps) {
  const { data: program, isLoading } = useProgram(programId);
  const batchUpdateDays = useBatchUpdateDays();
  const addDayCourse = useAddDayCourse();
  const removeDayCourse = useRemoveDayCourse();
  const updateProgram = useUpdateProgram();

  // ── Local day state ──────────────────────────────────────────────────────
  const [days, setDays] = useState<DayLocalState[]>([]);
  const [initialized, setInitialized] = useState(false);

  // ── Course picker modal ──────────────────────────────────────────────────
  const [coursePickerForDay, setCoursePickerForDay] = useState<number | null>(null);

  // ── Save state ───────────────────────────────────────────────────────────
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  // ── Duration control (owns duration_days + the day slots it scaffolds) ────
  const [durationInput, setDurationInput] = useState('');
  const [durationSaving, setDurationSaving] = useState(false);
  const [pendingShrink, setPendingShrink] = useState<{ next: number; lostDays: number[] } | null>(
    null
  );

  // ── Resolve signed URLs for day header images ────────────────────────────
  const resolveSignedUrl = useCallback(async (key: string): Promise<string | null> => {
    try {
      const result = await getSignedUrl({ key });
      return result.url;
    } catch {
      return null;
    }
  }, []);

  // ── Initialize local state from API data ─────────────────────────────────
  useEffect(() => {
    if (!program || initialized) return;

    const durationDays = program.duration_days;

    const initDays = async () => {
      const built: DayLocalState[] = [];

      for (let i = 1; i <= durationDays; i++) {
        const existingDay = program.days.find((d) => d.day_number === i);

        let previewUrl: string | null = null;
        if (existingDay?.header_image) {
          previewUrl = await resolveSignedUrl(existingDay.header_image);
        }

        built.push({
          dayId: existingDay?.id ?? null,
          dayNumber: i,
          title: existingDay?.title ?? `Day ${i}`,
          headerImageKey: existingDay?.header_image ?? null,
          headerImagePreviewUrl: previewUrl,
          headerImageFileName: existingDay?.header_image ? 'Day header' : null,
          headerImageFileSize: null,
          headerImageUploading: false,
          headerImageUploadProgress: 0,
          headerImageUploadError: null,
          courses: existingDay?.courses ?? [],
        });
      }

      setDays(built);
      setDurationInput(String(durationDays));
      setInitialized(true);
    };

    initDays();
  }, [program, initialized, resolveSignedUrl]);

  // Re-sync courses from API when data changes (after add/remove)
  useEffect(() => {
    if (!program || !initialized) return;

    setDays((prev) =>
      prev.map((dayLocal) => {
        const apiDay = program.days.find((d) => d.day_number === dayLocal.dayNumber);
        if (!apiDay) return dayLocal;
        return {
          ...dayLocal,
          dayId: apiDay.id,
          courses: apiDay.courses,
        };
      })
    );
  }, [program, initialized]);

  // ── Title change handler ─────────────────────────────────────────────────
  const handleTitleChange = useCallback((dayNumber: number, title: string) => {
    setDays((prev) =>
      prev.map((d) => (d.dayNumber === dayNumber ? { ...d, title } : d))
    );
  }, []);

  // ── Day image upload ─────────────────────────────────────────────────────
  const handleImageSelected = useCallback(
    async (dayNumber: number, files: File[]) => {
      const file = files[0];
      if (!file) return;

      if (!ALLOWED_IMAGE_TYPES.includes(file.type)) {
        setDays((prev) =>
          prev.map((d) =>
            d.dayNumber === dayNumber
              ? { ...d, headerImageUploadError: 'Only JPG, PNG, and WebP images are allowed' }
              : d
          )
        );
        return;
      }

      const localPreview = URL.createObjectURL(file);
      setDays((prev) =>
        prev.map((d) =>
          d.dayNumber === dayNumber
            ? {
                ...d,
                headerImageKey: null,
                headerImagePreviewUrl: localPreview,
                headerImageFileName: file.name,
                headerImageFileSize: file.size,
                headerImageUploading: true,
                headerImageUploadProgress: 0,
                headerImageUploadError: null,
              }
            : d
        )
      );

      try {
        const result = await uploadFile(
          { file, resource_type: 'program_day_header', resource_id: programId },
          {
            onUploadProgress: (progressEvent: AxiosProgressEvent) => {
              const pct = Math.round(
                (progressEvent.loaded * 100) / (progressEvent.total || 100)
              );
              setDays((prev) =>
                prev.map((d) =>
                  d.dayNumber === dayNumber
                    ? { ...d, headerImageUploadProgress: pct }
                    : d
                )
              );
            },
          }
        );

        setDays((prev) =>
          prev.map((d) =>
            d.dayNumber === dayNumber
              ? {
                  ...d,
                  headerImageKey: result.key,
                  headerImageUploading: false,
                  headerImageUploadProgress: 100,
                }
              : d
          )
        );
      } catch (err: any) {
        URL.revokeObjectURL(localPreview);
        setDays((prev) =>
          prev.map((d) =>
            d.dayNumber === dayNumber
              ? {
                  ...d,
                  headerImageKey: null,
                  headerImagePreviewUrl: null,
                  headerImageFileName: null,
                  headerImageFileSize: null,
                  headerImageUploading: false,
                  headerImageUploadProgress: 0,
                  headerImageUploadError:
                    err.response?.data?.message ?? 'Upload failed. Please try again.',
                }
              : d
          )
        );
      }
    },
    [programId]
  );

  const handleRemoveImage = useCallback((dayNumber: number) => {
    setDays((prev) =>
      prev.map((d) => {
        if (d.dayNumber !== dayNumber) return d;
        if (d.headerImagePreviewUrl) URL.revokeObjectURL(d.headerImagePreviewUrl);
        return {
          ...d,
          headerImageKey: null,
          headerImagePreviewUrl: null,
          headerImageFileName: null,
          headerImageFileSize: null,
          headerImageUploading: false,
          headerImageUploadProgress: 0,
          headerImageUploadError: null,
        };
      })
    );
  }, []);

  // ── Add course handler ───────────────────────────────────────────────────
  const handleAddCourse = useCallback((dayNumber: number) => {
    setCoursePickerForDay(dayNumber);
  }, []);

  const handleCourseSelected = useCallback(
    async (course: Course) => {
      if (coursePickerForDay === null) return;

      const day = days.find((d) => d.dayNumber === coursePickerForDay);
      if (!day?.dayId) {
        toast.error('Please save day details first.');
        return;
      }

      try {
        await addDayCourse.mutateAsync({
          programId,
          dayId: day.dayId,
          data: { course_id: course.id },
        });
      } catch (err: any) {
        toast.error(err.response?.data?.message ?? 'Failed to add course.');
      }
    },
    [coursePickerForDay, days, programId, addDayCourse]
  );

  // ── Remove course handler ────────────────────────────────────────────────
  const handleRemoveCourse = useCallback(
    async (dayNumber: number, dayCourse: ProgramDayCourse) => {
      const day = days.find((d) => d.dayNumber === dayNumber);
      if (!day?.dayId) return;

      try {
        await removeDayCourse.mutateAsync({
          programId,
          dayId: day.dayId,
          dayCourseId: dayCourse.id,
        });
      } catch (err: any) {
        toast.error(err.response?.data?.message ?? 'Failed to remove course.');
      }
    },
    [days, programId, removeDayCourse]
  );

  // ── Duration: persist + scaffold/prune the day slots ─────────────────────
  const persistDuration = useCallback(
    async (next: number, snapshotDays: DayLocalState[], snapshotInput: string) => {
      setDurationSaving(true);
      setDays((prev) => resizeDays(prev, next));
      setDurationInput(String(next));
      try {
        await updateProgram.mutateAsync({ programId, data: { duration_days: next } });
      } catch (err: unknown) {
        // Revert the optimistic resize (e.g. 409 when audiences already exist).
        setDays(snapshotDays);
        setDurationInput(snapshotInput);
        const message =
          (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
          'Could not change the duration. Please try again.';
        toast.error(message);
      } finally {
        setDurationSaving(false);
      }
    },
    [programId, updateProgram]
  );

  const commitDuration = useCallback(
    (rawNext: number) => {
      // Baseline off the rendered slot count, not program.duration_days — the
      // latter lags a tick behind each save (the query refetch is async), so a
      // quick second step would otherwise read a stale value.
      const current = days.length;
      const next = Math.max(MIN_DURATION, Math.min(MAX_DURATION, Math.round(rawNext)));
      setDurationInput(String(next));
      if (next === current) return;

      // Confirm before dropping tail days that already hold courses.
      if (next < current) {
        const lostDays = days
          .slice(next)
          .filter((d) => d.courses.length > 0)
          .map((d) => d.dayNumber);
        if (lostDays.length > 0) {
          setPendingShrink({ next, lostDays });
          return;
        }
      }
      void persistDuration(next, days, String(current));
    },
    [days, persistDuration]
  );

  const handleDurationBlur = useCallback(() => {
    const n = Number(durationInput);
    if (!durationInput.trim() || !Number.isFinite(n)) {
      setDurationInput(String(days.length));
      return;
    }
    commitDuration(n);
  }, [durationInput, days.length, commitDuration]);

  const handleDurationStep = useCallback(
    (delta: number) => {
      if (durationSaving) return;
      commitDuration(days.length + delta);
    },
    [durationSaving, days.length, commitDuration]
  );

  const confirmShrink = useCallback(() => {
    if (!pendingShrink) return;
    void persistDuration(pendingShrink.next, days, String(days.length));
    setPendingShrink(null);
  }, [pendingShrink, days, persistDuration]);

  const cancelShrink = useCallback(() => {
    setDurationInput(String(days.length));
    setPendingShrink(null);
  }, [days.length]);

  // ── Save day titles + images via batch update ────────────────────────────
  const handleSave = async () => {
    setSaving(true);
    try {
      await batchUpdateDays.mutateAsync({
        programId,
        days: days.map((d) => ({
          day_number: d.dayNumber,
          title: d.title.trim() || `Day ${d.dayNumber}`,
          header_image: d.headerImageKey ?? null,
        })),
      });
      setSaved(true);
      onSaved();
    } catch (err: any) {
      toast.error(err.response?.data?.message ?? 'Failed to save days. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // ── Loading state ────────────────────────────────────────────────────────
  if (isLoading || !initialized) {
    return (
      <div className="bg-surface-primary rounded-2xl border border-[rgba(71,70,76,0.1)] p-10 flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // ── Computed values for course picker ────────────────────────────────────
  const activeDayForPicker = days.find((d) => d.dayNumber === coursePickerForDay);
  const alreadyAddedCourseIds = new Set(
    (activeDayForPicker?.courses ?? []).map((dc) => dc.course_id)
  );

  // Duration stays editable with audiences (server allows it now); the
  // shrink-confirm dialog still guards days that hold courses.

  return (
    <>
      <StepCard
        stepNumber={2}
        title="Days & Courses"
        disabled={disabled}
        onSave={handleSave}
        saving={saving || durationSaving}
        saved={saved}
      >
        <div className="flex flex-col gap-6">
          <DurationControl
            value={durationInput}
            dayCount={days.length}
            saving={durationSaving}
            locked={false}
            onInputChange={setDurationInput}
            onCommitBlur={handleDurationBlur}
            onStep={handleDurationStep}
          />

          <div className="flex flex-col gap-5">
            {days.length === 0 ? (
              <p className="text-sm text-text-secondary text-center py-8">
                Set a duration above to add day slots.
              </p>
            ) : (
              <AnimatePresence initial={false}>
                {days.map((day) => (
                  <motion.div
                    key={day.dayNumber}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.2, ease: EASE_OUT }}
                  >
                    <DayCard
                      day={day}
                      onTitleChange={handleTitleChange}
                      onImageSelected={handleImageSelected}
                      onRemoveImage={handleRemoveImage}
                      onAddCourse={handleAddCourse}
                      onRemoveCourse={handleRemoveCourse}
                    />
                  </motion.div>
                ))}
              </AnimatePresence>
            )}
          </div>
        </div>
      </StepCard>

      {/* Course picker modal */}
      {coursePickerForDay !== null && (
        <CoursePickerModal
          dayNumber={coursePickerForDay}
          alreadyAddedCourseIds={alreadyAddedCourseIds}
          onSelect={handleCourseSelected}
          onClose={() => setCoursePickerForDay(null)}
        />
      )}

      {/* Destructive-shrink confirmation */}
      {pendingShrink && (
        <ShrinkConfirmModal
          nextDuration={pendingShrink.next}
          lostDays={pendingShrink.lostDays}
          onConfirm={confirmShrink}
          onCancel={cancelShrink}
        />
      )}
    </>
  );
}
